import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, MessageSquareWarning, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router';
import { driverDefenseService } from '../services/driverDefenseService';

const dayKeyOf = (item: any) => String(item.day || item.first_detected_at || item.last_detected_at || '').slice(0, 10);

const formatDay = (key: string) => {
  if (!key) return '-';
  const date = new Date(`${key}T00:00:00`);
  return Number.isNaN(date.getTime()) ? key : date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' });
};

const formatTime = (value: any) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

export function DriverMobileDefenses() {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<string | null>(null);

  const load = async () => {
    try { const response: any = await driverDefenseService.mine(); setItems(response?.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const groups = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const item of items) {
      const key = dayKeyOf(item);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, list]) => ({
        key,
        list,
        pendingIds: list.filter((entry: any) => entry.defense_status !== 'submitted').map((entry: any) => Number(entry.id)),
        submittedCount: list.filter((entry: any) => entry.defense_status === 'submitted').length,
        deadline: list.find((entry: any) => entry.defense_deadline_at)?.defense_deadline_at || null,
        daysLeft: list.find((entry: any) => entry.defense_days_left !== null && entry.defense_days_left !== undefined)?.defense_days_left ?? null,
        hasOtpPending: list.some((entry: any) => entry.defense_status === 'otp_pending'),
        allSubmitted: list.every((entry: any) => entry.defense_status === 'submitted'),
      }));
  }, [items]);

  const requestCode = async (group: any) => {
    const text = texts[group.key] || '';
    if (text.trim().length < 20) return alert('Savunmanızı en az 20 karakter olarak yazın.');
    setPending(group.key);
    try { await driverDefenseService.requestCode(group.pendingIds, text); await load(); alert('Doğrulama kodu telefonunuza gönderildi.'); }
    catch (error: any) { alert(error?.message || 'Kod gönderilemedi'); }
    finally { setPending(null); }
  };
  const verify = async (group: any) => {
    setPending(group.key);
    try { await driverDefenseService.verify(group.pendingIds, codes[group.key] || ''); await load(); alert('Savunmanız gönderildi.'); }
    catch (error: any) { alert(error?.message || 'Kod doğrulanamadı'); }
    finally { setPending(null); }
  };

  const renderDeadline = (group: any) => {
    if (group.allSubmitted) return null;
    const deadline = group.deadline ? new Date(group.deadline) : null;
    if (!deadline || Number.isNaN(deadline.getTime())) return null;
    const days = group.daysLeft;
    const overdue = typeof days === 'number' && days < 0;
    const label = overdue ? 'Savunma süresi doldu' : days === 0 ? 'Son gün' : `${days} iş günü kaldı`;
    return <p className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${overdue ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`}>Termin: {deadline.toLocaleDateString('tr-TR')} · {label}</p>;
  };

  return <div className="min-h-screen bg-slate-50 pb-8">
    <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-4"><button onClick={() => navigate('/driver-mobile-home')}><ArrowLeft className="h-5 w-5" /></button><div><h1 className="font-bold text-slate-900">Savunma Talepleri</h1><p className="text-xs text-slate-500">Aynı güne ait ağır ihlaller için tek savunma yazıp SMS koduyla onaylayın.</p></div></header>
    <main className="space-y-4 p-4">
      {loading
        ? <div className="py-12 text-center text-slate-500"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        : groups.length === 0
          ? <div className="rounded-2xl bg-white p-6 text-center text-sm text-slate-500">Yanıt bekleyen ağır ihlal bulunmuyor.</div>
          : groups.map((group) => <section key={group.key} className="rounded-2xl border border-rose-100 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold text-slate-900">{formatDay(group.key)}</p>
                <span className="shrink-0 text-xs text-slate-500">{group.list.length} ağır ihlal{group.submittedCount ? ` · ${group.submittedCount} gönderildi` : ''}</span>
              </div>
              {renderDeadline(group)}
              <div className="mt-3 space-y-2">
                {group.list.map((item: any) => <div key={item.id} className="flex gap-2 rounded-xl bg-slate-50 p-3">
                  <MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{item.defense_title || item.violation_type.replaceAll('_', ' ')}{item.defense_status === 'submitted' ? ' · gönderildi' : ''}</p>
                    <p className="text-xs text-slate-500">{item.tractor_plate || '-'} · {formatTime(item.last_detected_at)}</p>
                    {item.defense_message
                      ? <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-slate-600">{item.defense_message}</p>
                      : item.description ? <p className="mt-1 text-xs text-slate-600">{item.description}</p> : null}
                  </div>
                </div>)}
              </div>
              {group.allSubmitted
                ? <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-medium text-emerald-700"><ShieldCheck className="h-4 w-4" />Bu günün savunması gönderildi.</div>
                : <div className="mt-4 space-y-3">
                    <textarea value={texts[group.key] ?? ''} onChange={(event) => setTexts({ ...texts, [group.key]: event.target.value })} rows={5} className="w-full rounded-xl border border-slate-300 p-3 text-sm" placeholder="Bu gündeki tüm ağır ihlaller için ortak savunmanızı yazın..." />
                    {group.hasOtpPending
                      ? <div className="flex gap-2"><input value={codes[group.key] || ''} onChange={(event) => setCodes({ ...codes, [group.key]: event.target.value })} inputMode="numeric" maxLength={6} className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3" placeholder="6 haneli kod" /><button onClick={() => void verify(group)} disabled={pending === group.key} className="rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white">{pending === group.key ? 'Onaylanıyor...' : 'Onayla'}</button></div>
                      : <button onClick={() => void requestCode(group)} disabled={pending === group.key} className="w-full rounded-xl bg-rose-600 py-3 text-sm font-semibold text-white">{pending === group.key ? 'Gönderiliyor...' : `Toplu Savunma Gönder (${group.pendingIds.length})`}</button>}
                  </div>}
            </section>)}
    </main>
  </div>;
}
